/* eslint-disable no-nested-ternary */
import DOMPurify from "dompurify";
import { FC, ReactNode } from "react";

import tourismLegalStyles from "web/Pages/Checkout/TourismDomestic/SharedComponents/LegalInformation/legalInformation.scss";

import __ from "web/Layout/Translations";

import Accordion from "web/Components/Common/Accordion";
import type { AccordionVariant } from "web/Components/Common/Accordion/accordion";
import HtmlTemplate from "web/Components/Common/HtmlTemplate";

import { Classes } from "web/types/Common";
import { Functionality } from "web/types/Employee";

import { useAppContext } from "web/context/app";
import {
  useGetEmployeeConfigurationQuery,
  useGetEmployeeDetailsQuery,
} from "web/features/employee/employeeApiSlice";

import mobileLegalsClasses from "./orderDetailsLegalsMobile.scss";

export interface IOrderDetailsLegalAccordionsProps {
  withdrawalContent?: string | null;
  withdrawalTemplateVariables?: {
    providerName: string;
    providerRegulations: string;
  };
  tourismVariant?: boolean;
  itemClassName?: string;
  accordionClassName?: string;
  contentClassName?: string;
  accordionClasses?: Classes;
}

const OrderDetailsLegalAccordions: FC<IOrderDetailsLegalAccordionsProps> = ({
  withdrawalContent,
  withdrawalTemplateVariables,
  tourismVariant = false,
  itemClassName,
  accordionClassName,
  contentClassName,
  accordionClasses,
}) => {
  const { isMobile } = useAppContext();
  const { data: employeeDetails } = useGetEmployeeDetailsQuery();
  const { data: employeeConfig } = useGetEmployeeConfigurationQuery();

  const isRightOfWithdrawalEnabled = employeeDetails?.functionalities?.includes(
    Functionality.ENABLE_RIGHT_OF_WITHDRAWAL
  );

  const contractualContent =
    isRightOfWithdrawalEnabled && employeeConfig?.rightOfWithdrawal
      ? employeeConfig.rightOfWithdrawal
      : null;

  if (!withdrawalContent && !contractualContent) {
    return null;
  }

  const isUniversalCodeOrderDetails = !tourismVariant;

  const variant: AccordionVariant = tourismVariant
    ? isMobile
      ? "tertiary"
      : "primary"
    : "primary";

  const tourismAccordionRootClass =
    tourismVariant && !isMobile ? tourismLegalStyles.accordionRoot : undefined;

  const renderWithdrawalBody = () => {
    if (!withdrawalContent) {
      return null;
    }

    if (withdrawalTemplateVariables) {
      return (
        <HtmlTemplate
          template={withdrawalContent}
          variables={withdrawalTemplateVariables}
          className={tourismVariant ? tourismLegalStyles.text : undefined}
        />
      );
    }

    return (
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(withdrawalContent),
        }}
      />
    );
  };

  const wrapMobileContent = (content: ReactNode) => {
    if (isUniversalCodeOrderDetails && contentClassName) {
      return <div className={contentClassName}>{content}</div>;
    }

    return content;
  };

  const wrapDesktopItem = (accordion: ReactNode) => {
    if (isUniversalCodeOrderDetails && !isMobile && itemClassName) {
      return <div className={itemClassName}>{accordion}</div>;
    }

    return accordion;
  };

  const wrapDesktopContent = (content: ReactNode) => {
    if (isUniversalCodeOrderDetails && !isMobile) {
      return <div className="reset-global-styles">{content}</div>;
    }

    if (tourismVariant) {
      return <p>{content}</p>;
    }

    return content;
  };

  const renderContractualBody = () => (
    <span
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(contractualContent!),
      }}
    />
  );

  const withdrawalAccordion = withdrawalContent ? (
    <Accordion
      label={__("Odstąpienie od umowy")}
      arrow
      variant={variant}
      className={
        isUniversalCodeOrderDetails
          ? accordionClassName
          : tourismAccordionRootClass
      }
      classes={
        isUniversalCodeOrderDetails && isMobile ? accordionClasses : undefined
      }
    >
      {isUniversalCodeOrderDetails && isMobile
        ? wrapMobileContent(renderWithdrawalBody())
        : wrapDesktopContent(renderWithdrawalBody())}
    </Accordion>
  ) : null;

  const contractualAccordion = contractualContent ? (
    <Accordion
      label={__("Umowne prawo anulacji")}
      arrow
      variant={variant}
      className={
        isUniversalCodeOrderDetails
          ? accordionClassName
          : tourismAccordionRootClass
      }
      classes={
        isUniversalCodeOrderDetails && isMobile ? accordionClasses : undefined
      }
    >
      {isUniversalCodeOrderDetails && !isMobile
        ? wrapDesktopContent(renderContractualBody())
        : tourismVariant
          ? <p>{renderContractualBody()}</p>
          : wrapMobileContent(renderContractualBody())}
    </Accordion>
  ) : null;

  const accordions = (
    <>
      {withdrawalAccordion
        ? wrapDesktopItem(withdrawalAccordion)
        : null}
      {contractualAccordion
        ? wrapDesktopItem(contractualAccordion)
        : null}
    </>
  );

  if (isUniversalCodeOrderDetails && isMobile) {
    return <div className={mobileLegalsClasses.legalsGroup}>{accordions}</div>;
  }

  return accordions;
};

export default OrderDetailsLegalAccordions;
